import React, { FC, useCallback, useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import api from '../../api';
import type {
  ConnectedAppKey,
  OrganisationConnectedAppIdentitiesResponse,
  OrganisationConnectedAppIdentityCandidate,
  OrganisationConnectedAppIdentityCandidatesResponse,
  OrganisationConnectedAppIdentityResponse,
  OrganisationConnectedAppMemberIdentity,
  OrganisationConnectionIdentitySource,
  SetOrganisationConnectedAppIdentityRequest,
} from '@dreamai/types';
import { ORGANISATION_IDENTITY_QUERY_MIN_LENGTH } from '@dreamai/types';
import { apiErrorMessage, formatDate } from './organisationLabels';
import { AssistantCopy, assistantCopy } from './organisationAssistantCopy';

/**
 * Which record in an accounting app each member's expense claims are filed
 * against. Reve works this out itself (match by email, else create a record)
 * and stops when the app is ambiguous; an admin sees the result per member
 * here, picks the right record from the app, or clears a mapping so Reve
 * matches afresh on the member's next expense.
 */

type Feedback = { type: 'success' | 'error'; message: string } | null;

interface OrganisationConnectedAppIdentitiesProps {
  organisationId: string;
  appKey: ConnectedAppKey;
  appLabel: string;
  /** See OrganisationConnectedApps: name Reve or the members' own assistants. */
  teamAssistantsAvailable: boolean;
  /** The parent is mid-request; hold our own actions too. */
  disabled: boolean;
}

const sourceBadges = (
  assistant: AssistantCopy,
): Record<
  OrganisationConnectionIdentitySource | 'unmapped',
  { label: string; tone: 'on' | 'admin' | 'off' }
> => ({
  email_match: { label: 'Matched by email', tone: 'on' },
  created: { label: `Created by ${assistant.theirs}`, tone: 'on' },
  admin: { label: 'Set by admin', tone: 'admin' },
  unmapped: { label: 'Unmapped', tone: 'off' },
});

const OrganisationConnectedAppIdentities: FC<
  OrganisationConnectedAppIdentitiesProps
> = ({
  organisationId,
  appKey,
  appLabel,
  teamAssistantsAvailable,
  disabled,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const base = `/organisations/${organisationId}/connected-apps/${appKey}/identities`;
  const assistant = assistantCopy(teamAssistantsAvailable);
  const badges = sourceBadges(assistant);

  const [members, setMembers] = useState<
    OrganisationConnectedAppMemberIdentity[] | null
  >(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<Feedback>(null);
  const [busy, setBusy] = useState(false);
  const [picking, setPicking] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [candidates, setCandidates] = useState<{
    query: string;
    items: OrganisationConnectedAppIdentityCandidate[];
  } | null>(null);

  const authHeaders = useCallback(async () => {
    const token = await getAccessTokenSilently();
    return { headers: { Authorization: `Bearer ${token}` } };
  }, [getAccessTokenSilently]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } =
          await api.get<OrganisationConnectedAppIdentitiesResponse>(
            base,
            await authHeaders(),
          );
        if (!cancelled) setMembers(data.members);
      } catch (err) {
        if (cancelled) return;
        console.error('Error loading claimant identities:', err);
        setLoadError(
          apiErrorMessage(err, `Could not load who is who in ${appLabel}.`),
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [base, authHeaders, appLabel]);

  const replaceMember = (member: OrganisationConnectedAppMemberIdentity) =>
    setMembers((current) =>
      current
        ? current.map((m) =>
            m.membershipId === member.membershipId ? member : m,
          )
        : current,
    );

  const startPicking = (membershipId: string) => {
    setPicking(membershipId);
    setQuery('');
    setCandidates(null);
    setFeedback(null);
  };

  const stopPicking = () => {
    setPicking(null);
    setQuery('');
    setCandidates(null);
  };

  const search = async (e: React.FormEvent) => {
    e.preventDefault();
    const q = query.trim();
    if (q.length < ORGANISATION_IDENTITY_QUERY_MIN_LENGTH) return;
    setBusy(true);
    setFeedback(null);
    try {
      const { data } =
        await api.get<OrganisationConnectedAppIdentityCandidatesResponse>(
          `${base}/candidates`,
          { params: { query: q }, ...(await authHeaders()) },
        );
      setCandidates({ query: data.query, items: data.candidates });
    } catch (err) {
      console.error('Error searching claimant candidates:', err);
      setFeedback({
        type: 'error',
        message: apiErrorMessage(err, `Could not search ${appLabel}.`),
      });
    } finally {
      setBusy(false);
    }
  };

  const choose = async (
    member: OrganisationConnectedAppMemberIdentity,
    candidate: OrganisationConnectedAppIdentityCandidate,
  ) => {
    setBusy(true);
    try {
      const body: SetOrganisationConnectedAppIdentityRequest = {
        providerIdentityKind: candidate.kind,
        providerIdentityId: candidate.id,
        providerIdentityLabel: candidate.label,
      };
      const { data } = await api.put<OrganisationConnectedAppIdentityResponse>(
        `${base}/${member.membershipId}`,
        body,
        await authHeaders(),
      );
      replaceMember(data.member);
      stopPicking();
      setFeedback({
        type: 'success',
        message: `${member.name}’s expenses will be filed against “${candidate.label}” in ${appLabel}. If one of their expenses failed over this, they can ask ${assistant.theirs} to file it again.`,
      });
    } catch (err) {
      console.error('Error setting claimant identity:', err);
      setFeedback({
        type: 'error',
        message: apiErrorMessage(
          err,
          `Could not set ${member.name}’s ${appLabel} record.`,
        ),
      });
    } finally {
      setBusy(false);
    }
  };

  const clear = async (member: OrganisationConnectedAppMemberIdentity) => {
    setBusy(true);
    try {
      const { data } =
        await api.delete<OrganisationConnectedAppIdentityResponse>(
          `${base}/${member.membershipId}`,
          await authHeaders(),
        );
      replaceMember(data.member);
      if (picking === member.membershipId) stopPicking();
      setFeedback({
        type: 'success',
        message: `Cleared ${member.name}’s ${appLabel} record. ${assistant.Theirs} will match them afresh on their next expense.`,
      });
    } catch (err) {
      console.error('Error clearing claimant identity:', err);
      setFeedback({
        type: 'error',
        message: apiErrorMessage(
          err,
          `Could not clear ${member.name}’s ${appLabel} record.`,
        ),
      });
    } finally {
      setBusy(false);
    }
  };

  const held = disabled || busy;
  const mapped = members?.filter((m) => m.identity !== null).length ?? 0;

  return (
    <details
      className='organisation-details organisation-identities'
      data-testid={`connected-app-${appKey}-identities`}
    >
      <summary>
        Identities in {appLabel}
        {members ? ` (${mapped} of ${members.length} mapped)` : ''}
      </summary>
      <p className='organisation-group-meta'>
        The {appLabel} record each member’s expense claims are filed against.{' '}
        {assistant.Theirs} matches them by email or creates a record for them;
        set one by hand when {appLabel} has several records for a member or{' '}
        {assistant.theirs} could not decide.
      </p>

      {feedback && (
        <div
          className={`organisation-feedback ${feedback.type}`}
          role={feedback.type === 'error' ? 'alert' : 'status'}
        >
          {feedback.message}
        </div>
      )}

      {loadError ? (
        <p className='organisation-empty' role='alert'>
          {loadError}
        </p>
      ) : members === null ? (
        <p className='organisation-empty'>Loading…</p>
      ) : members.length === 0 ? (
        <p className='organisation-empty'>No active members.</p>
      ) : (
        <ul className='organisation-group-list'>
          {members.map((member) => {
            const badge = badges[member.identity?.source ?? 'unmapped'];
            return (
              <li
                key={member.membershipId}
                className='organisation-group-item'
                data-testid={`connected-app-${appKey}-identity-${member.membershipId}`}
              >
                <div className='organisation-group-row'>
                  <div className='organisation-group-info'>
                    <span className='organisation-group-name'>
                      {member.name}
                    </span>
                    <span className='organisation-group-meta'>
                      <span
                        className={`organisation-identity-badge ${badge.tone}`}
                      >
                        {badge.label}
                      </span>
                      {member.identity
                        ? ` · ${member.identity.providerIdentityLabel ?? member.identity.providerIdentityId} · ${formatDate(member.identity.matchedAt)}`
                        : ` · ${assistant.Theirs} matches or creates a record on their first expense.`}
                    </span>
                  </div>
                  <div className='organisation-identity-actions'>
                    <button
                      type='button'
                      className='btn-link'
                      aria-label={`Change ${appLabel} record for ${member.name}`}
                      disabled={held}
                      onClick={() =>
                        picking === member.membershipId
                          ? stopPicking()
                          : startPicking(member.membershipId)
                      }
                    >
                      {picking === member.membershipId ? 'Cancel' : 'Change'}
                    </button>
                    {member.identity && (
                      <button
                        type='button'
                        className='btn-link'
                        aria-label={`Clear ${appLabel} record for ${member.name}`}
                        disabled={held}
                        onClick={() => clear(member)}
                      >
                        Clear
                      </button>
                    )}
                  </div>
                </div>

                {picking === member.membershipId && (
                  <form
                    className='organisation-identity-picker'
                    onSubmit={search}
                  >
                    <div className='organisation-field-row'>
                      <input
                        className='field-input'
                        type='search'
                        aria-label={`Search ${appLabel} records for ${member.name}`}
                        placeholder={`Name or email (at least ${ORGANISATION_IDENTITY_QUERY_MIN_LENGTH} characters)`}
                        value={query}
                        disabled={held}
                        onChange={(e) => setQuery(e.target.value)}
                        autoFocus
                      />
                      <button
                        type='submit'
                        className='btn-secondary'
                        disabled={
                          held ||
                          query.trim().length <
                            ORGANISATION_IDENTITY_QUERY_MIN_LENGTH
                        }
                      >
                        {busy ? 'Searching…' : 'Search'}
                      </button>
                    </div>
                    {candidates &&
                      (candidates.items.length === 0 ? (
                        <p className='organisation-empty'>
                          No {appLabel} records match “{candidates.query}”.
                        </p>
                      ) : (
                        <ul className='organisation-group-list'>
                          {candidates.items.map((candidate) => (
                            <li
                              key={`${candidate.kind}:${candidate.id}`}
                              className='organisation-group-item'
                            >
                              <div className='organisation-group-row'>
                                <div className='organisation-group-info'>
                                  <span className='organisation-group-name'>
                                    {candidate.label}
                                  </span>
                                  <span className='organisation-group-meta'>
                                    {candidate.email ?? 'No email on record'}
                                  </span>
                                </div>
                                <button
                                  type='button'
                                  className='btn-secondary'
                                  aria-label={`Use ${candidate.label} for ${member.name}`}
                                  disabled={held}
                                  onClick={() => choose(member, candidate)}
                                >
                                  Use
                                </button>
                              </div>
                            </li>
                          ))}
                        </ul>
                      ))}
                  </form>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </details>
  );
};

export default OrganisationConnectedAppIdentities;
